import { useTranslation } from 'react-i18next';
import { Fragment, useState, useContext, useEffect } from 'react';
import {
  PersonAvatar,
  PageHeader,
  IconButton,
  Button,
  ButtonLink,
  Tabs,
  InputText,
  FilterList,
  Tag,
  TEOLoader,
} from '@teo/components';
import { Search, ArrowRight, MoreVertical, Eye } from '@teo/components/icons';
import { useGet } from '@/query/fetchHooks';
import { useNavigate, useParams, Link } from 'react-router-dom';
import i18n from 'i18next';
import { format } from 'date-fns';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { DataTable } from '@/components/DataTable/DataTable';
import OptionsAction from '@/components/DataTable/OptionsAction';
import { Studentwithteams, Userwithorganisationwithidps } from '@/models';
import { newUid } from '@/util';
import { useMediaQuery } from 'react-responsive';
import { axios } from '@/axios';
import { config } from '@/config';
import { TeamsTable } from '@/components/TeamsTable';
import { BreadCrumbsDetails } from '@/components/BreadCrumbsDetails';

export interface Filter {
  label: string;
  value: string;
}

function LearningPathsVersions() {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;
  const { organisationId, learningpathId }: any = useParams();
  const [tab, setTab] = useState('versions');
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilter(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);
  const [filterStatus, setFilterStatus] = useState<any>();
  const [refetchTable, setRefetchTable] = useState<string>(newUid(20));

  const [learningpath, setLearningpath] = useState<any>();
  const [selectedStatus, setSelectedStatus] = useState<Filter[]>([
    {
      label: t('pages.follow_up_courses.status_active'),
      value: 'active',
    },
  ]);

  const [preloader, setPreloader] = useState(true);
  const [loaderEd, setLoaderEd] = useState(true);

  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: '(min-width: 1200px)' });

  const basePath = window.location.pathname.slice(
    0,
    window.location.pathname.lastIndexOf('/')
  );

  const userPath = basePath.slice(0, basePath.lastIndexOf('/')) + '/users';
  const teamPath = basePath.slice(0, basePath.lastIndexOf('/')) + '/teams';

  const managePath = `/${i18n.language}/manage/${organisationId}/users`;

  const goRouteId = (url: any) => {
    navigate(url);
  };

  useGet(`/myusedlps`, {
    organisationId: authUser?.activeOrganisationId,
    userId: authUser?.id,
    learningpathId: learningpathId,
    onSuccess: (res: any) => {
      setLearningpath(res?.data[0] || undefined);
      setPreloader(false);
    },
    onError: (res: any) => {
      console.error('res', res);
      setPreloader(false);
    },
  });

  const FILTERS_STATUS = [
    {
      label: t('pages.follow_up_courses.status_active'),
      value: 'active',
    },
    {
      label: t('pages.follow_up_courses.status_inactive'),
      value: 'disabled,hidden',
    },
  ];

  useEffect(() => {
    if (selectedStatus.length == 0) {
      setFilterStatus(null);
    }
    if (selectedStatus.length == 1) {
      setFilterStatus(`${selectedStatus[0].value}`);
    }
    if (selectedStatus.length > 1) {
      const select = selectedStatus?.map((el) => el.value);
      setFilterStatus(`${select}`);
    }
    setRefetchTable(newUid(20));
  }, [selectedStatus]);

  const userColumns: any = [
    {
      title: t('columns.username'),
      id: 'username',
      className: 'col-start-1 col-end-9',
      render: (usr: Studentwithteams) => (
        <div className="flex items-center">
          <PersonAvatar
            inline
            id={usr.id}
            name={usr.username || ''}
            imageSrc={usr.picture || undefined}
          ></PersonAvatar>
          <div>
            <div className="text-sm font-medium">{usr.username}</div>
            <div className="text-xs text-[#7d7d81]">{usr.email}</div>
          </div>
        </div>
      ),
    },
  ];

  if (
    Math.max(authUser?.role || 0, authUser?.activeOrganisationRole || 0) >= 90
  ) {
    userColumns.push({
      id: 'actionButton',
      sortable: false,
      title: '',
      className: 'col-start-9 row-start-1 justify-end',
      render: (usr: any) => (
        <OptionsAction
          menuItems={[
            {
              addIcon: true,
              id: '2',
              title: t('pages.follow_up_courses.manage_user'),
              onClick: () => navigate(`${managePath}/${usr?.id}`),
            },
          ]}
          screensUpdateTable="planshet"
          pageDetail={{
            title: t('pages.follow_up_courses.manage_user'),
            rowPath: (usr) => `${userPath}/${usr.id}`,
          }}
          rowInfo={usr}
          mobileHeader={{
            render: (usr: any) => (
              <div className="flex items-center">
                <PersonAvatar
                  inline
                  id={usr.id}
                  name={usr.username || ''}
                  imageSrc={usr.picture || undefined}
                ></PersonAvatar>
                <div>
                  <div className="text-sm font-medium">{usr.username}</div>
                  <div className="text-xs text-[#7d7d81]">{usr.email}</div>
                </div>
              </div>
            ),
          }}
        />
      ),
    });
  }

  return (
    <>
      <div className="w-full max-w-6xl flex-col pb-8">
        {preloader ? (
          <div className="fixed inset-0 z-[101] flex bg-white/75">
            <div className="m-auto">
              <TEOLoader
                loaderSrc="/images/TEOloader.gif"
                className="w-[110px]"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="pb-2">
              <BreadCrumbsDetails
                prevPathname={basePath}
                prevName={t(`breadcrumbs.LEARNINGPATH`)}
                pathname={window.location.pathname}
                name={learningpath?.lpName}
              />
              <PageHeader /* @ts-expect-error eslint-disable-next-line */
                to={-1}
                ariaLabel={'placeholder'}
              >
                <div className="flex w-full items-center py-4 lg:py-6">
                  <div className="flex items-center">
                    <div className="w-16 min-w-16 lg:w-24 lg:min-w-24">
                      <img
                        src={
                          learningpath?.lpPicture ||
                          '/assets/images/LearnPath.png'
                        }
                        alt={learningpath?.lpName}
                        className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                      />
                    </div>
                  </div>

                  <div className="mx-4 flex w-full flex-col justify-between gap-4 xl:flex-row">
                    <div className="flex flex-col content-center">
                      <h1 className="break-all pb-1.5 text-base font-semibold text-black md:text-lg lg:text-xl">
                        {learningpath?.lpName}
                      </h1>
                      <div className="flex flex-wrap content-center text-xs text-[#919193]">
                        <p className="mr-3 border-r border-[#919193] pr-3 ">
                          {t(`courseTypes.learning-path`)}
                        </p>
                        <span className="">
                          {t('pages.follow_up_courses.courses_author')}
                          <Link
                            className="inline-block pl-2 pr-2 text-xs text-tint-blue hover:underline"
                            to="../#"
                          >
                            {learningpath?.username}
                          </Link>
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <div className="rounded-lg shadow-[inset_0_0_0_1px] shadow-grey-03">
                        <ButtonLink
                          to={`/${i18n.language}/create/${organisationId}/learning-paths/${learningpathId}`}
                          variant="outline"
                          size="md"
                          className="flex items-center shadow-[inset_0_0_0_1px]"
                        >
                          {t('pages.learning_path_details.edit_lp')}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </ButtonLink>
                      </div>

                      <a
                        target="_blank"
                        rel="noreferrer"
                        href={`/${i18n.language}/learn/${organisationId}/learning-paths/${learningpathId}/preview`}
                      >
                        <Button className="flex items-center">
                          {t('button.preview')}
                          <Eye className="ml-2 h-5 w-5" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </PageHeader>
            </div>
            <div className="mt-6 font-medium">
              <Tabs activeName={tab} onChange={(tab) => setTab(tab)}>
                <Tabs.TabButton name="versions">
                  {t('columns.editions')}
                </Tabs.TabButton>
                <Tabs.TabButton name="users">
                  <span>{t('pages.follow_up_courses.users')}</span>
                </Tabs.TabButton>
                <Tabs.TabButton name="teams">
                  {t('pages.follow_up_courses.teams')}
                </Tabs.TabButton>
              </Tabs>
            </div>
            {tab === 'versions' && (
              <>
                <div className="py-8 md:w-1/2 xl:w-1/3">
                  <InputText
                    value={filter}
                    onChange={(ev) => setFilter(ev.target.value)}
                    Icon={Search}
                    placeholder={t('search.learningpath')}
                  />
                </div>

                <div className="mb-5 flex items-center">
                  <FilterList
                    filters={FILTERS_STATUS}
                    onSelect={setSelectedStatus}
                    selected={selectedStatus}
                  />
                </div>
                <DataTable
                  screensUpdateTable="planshet"
                  key={refetchTable}
                  filter={debouncedFilter}
                  fetch={useGet.bind(null, '/memberlpgroupdetails', {
                    masterId: `${learningpathId},OR,learningpathId=${learningpathId}`,
                    organisationId: authUser?.activeOrganisationId,
                    lpStatus: filterStatus,
                  })}
                  empty={{
                    template: 2,
                    text: t('pages.follow_learningpath.lp_list_empty'),
                  }}
                  defaultSort={[
                    { id: '-updatedAt', desc: false },
                    { id: 'lpName', desc: false },
                  ]}
                  rowPath={(learningpath: any) =>
                    `${basePath}/details/${learningpath.id}`
                  }
                  columns={[
                    {
                      title: t('columns.edition'),
                      id: 'lpName',
                      className: 'col-start-1 col-end-8 row-start-1',
                      render: (learningpath: any) => (
                        <div className="flex items-center">
                          <div className="mr-3 w-16 min-w-16 sm:w-20 sm:min-w-20">
                            <img
                              src={
                                learningpath?.lpgroupPicture ||
                                learningpath?.lpPicture ||
                                '/assets/images/LearnPath.png'
                              }
                              alt={learningpath?.lpName}
                              className="inline h-12 w-full rounded-lg object-cover sm:h-14"
                            />
                          </div>
                          <div className="flex flex-col">
                            {learningpath?.lpgroupName || learningpath?.lpName}
                            <span className="text-xs text-grey-06">
                              {t('columns.version')}{' '}
                              {learningpath?.version
                                ? learningpath?.version
                                : 0}
                            </span>
                          </div>
                        </div>
                      ),
                    },

                    {
                      addTitleOnMobile: true,
                      id: 'nrOfTeams',
                      title: t('columns.teams'),
                      className: 'col-start-1 col-end-4 row-start-2',
                      align: 'right',
                      sortable: false,
                      render: (learningpath: any) => (
                        <div>
                          {learningpath?.nrOfTeams
                            ? learningpath?.nrOfTeams
                            : 0}
                        </div>
                      ),
                    },

                    {
                      addTitleOnMobile: true,
                      id: 'nrOfStudents',
                      title: t('columns.enrolled'),
                      className:
                        'col-start-4 col-end-7 row-start-2 justify-center text-center',
                      align: 'right',
                    },

                    {
                      addTitleOnMobile: true,
                      className:
                        'col-start-8 col-end-10 row-start-2 justify-end',
                      id: 'createdAt',
                      title: t('columns.created'),
                      render: (learningpath: any) =>
                        format(new Date(learningpath.createdAt), 'dd/MM/yyyy'),
                      align: 'right',
                    },
                    {
                      addTitleOnMobile: true,
                      id: 'lpStatus',
                      title: t('columns.status'),
                      className: 'col-start-1 col-end-4 row-start-3',
                      align: 'right',
                      render: (learningpath: any) => (
                        <Tag
                          title={t(
                            learningpath.lpStatus === 'active'
                              ? t('button.open')
                              : t('button.close')
                          )}
                          variant={
                            learningpath.lpStatus === 'active'
                              ? 'success'
                              : 'error'
                          }
                          type="yellow"
                        />
                      ),
                    },
                    {
                      addTitleOnMobile: true,
                      id: 'closeTeam',
                      sortable: false,
                      title: '',
                      className:
                        'col-start-5 col-end-10 row-start-3 justify-end',
                      align: 'right',
                      classNameDesktop: 'max-w-[125px] w-[125px]',
                      render: (learningpath: any) => (
                        <div className="flex justify-end">
                          {isDesktop ? (
                            learningpath?.lpStatus !== 'disabled' && (
                              <Button
                                variant="destruct"
                                size="md"
                                className="flex items-center"
                                onClick={async (event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  const result = await axios
                                    .put(
                                      `${config.backend}/lpgroup/${learningpath.id}`,
                                      {
                                        lpStatus: 'disabled',
                                      }
                                    )
                                    .then(() => setRefetchTable(newUid(20)));
                                  //   props.setCourse(Object.assign({}, course, result?.data));
                                }}
                              >
                                {t('button.close')}
                              </Button>
                            )
                          ) : (
                            <Button
                              className="relative z-[2] flex items-center"
                              variant="outline"
                              size="sm"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                const rowPath = `${basePath}/details/${learningpath.id}`;
                                goRouteId(rowPath);
                                window.scrollTo(0, 0);
                              }}
                            >
                              {t('pages.follow_up.to_details')}
                            </Button>
                          )}
                        </div>
                      ),
                    },
                  ]}
                />
              </>
            )}
            {tab === 'users' && (
              <Fragment>
                <div className="py-8 md:w-1/2 xl:w-1/3">
                  <InputText
                    value={filter}
                    onChange={(ev) => setFilter(ev.target.value)}
                    Icon={Search}
                    placeholder={t('search.user')}
                  />
                </div>

                <DataTable
                  filter={debouncedFilter}
                  rowPath={(user) => `${userPath}/${user.id}`}
                  defaultSort={[{ id: 'username', desc: false }]}
                  fetch={useGet.bind(null, '/alllpusers', {
                    masterId: learningpathId,
                    organisationId: authUser?.activeOrganisationId,
                  })}
                  screensUpdateTable="planshet"
                  empty={{
                    template: 1,
                    text: t('empty_tenplate.user_list'),
                    btn_text: t('empty_tenplate.btn_add_user'),
                    btnClick: () => navigate(userPath),
                  }}
                  columns={userColumns}
                />
              </Fragment>
            )}
            {tab === 'teams' && (
              <Fragment>
                <div className="py-8 md:w-1/2 xl:w-1/3">
                  <InputText
                    value={filter}
                    onChange={(ev) => setFilter(ev.target.value)}
                    Icon={Search}
                    placeholder={t('search.team')}
                  />
                </div>
                <TeamsTable
                  filter={debouncedFilter}
                  rowPath={(team: any) => `${teamPath}/${team.id}`}
                  defaultSort={[{ id: 'teamPath', desc: false }]}
                  fetch={useGet.bind(null, '/alllpteams', {
                    masterId: learningpathId,
                    organisationId: authUser.activeOrganisationId,
                  })}
                  screensUpdateTable="planshet"
                  organisationId={organisationId}
                  empty={{
                    template: 1,
                    text: t('empty_tenplate.team_list'),
                    btn_text: t('empty_tenplate.btn_add_team'),
                    btnClick: () => navigate(teamPath),
                  }}
                />
              </Fragment>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default LearningPathsVersions;
